/**
 * "Frequently asked questions" — Introduction section.
 *
 * A plain-language Q&A digest for the reader who won't read the docs linearly —
 * especially a non-technical buyer or admin. Every answer is a short, honest
 * summary of content stated fully elsewhere, with a link to the deeper page.
 * Grouped by the questions people actually ask: getting started, what the
 * assistant does, security & data, the widget, and billing & languages.
 */
import type { DocPageContent } from '../types';

const QA = (k: string) => ({
  termKey: `docs.faq.q.${k}.q`,
  descKey: `docs.faq.q.${k}.a`,
});

export const FAQ_PAGE: DocPageContent = {
  slug: 'faq',
  titleKey: 'docs.faq.title',
  descriptionKey: 'docs.faq.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.faq.lead' },

    { kind: 'heading', level: 2, id: 'getting-started', textKey: 'docs.faq.group.start.heading' },
    {
      kind: 'definitions',
      items: [QA('needDeveloper'), QA('howLong'), QA('needBefore'), QA('missEndpoint')],
    },

    { kind: 'heading', level: 2, id: 'what-it-does', textKey: 'docs.faq.group.does.heading' },
    {
      kind: 'definitions',
      items: [QA('whatCanDo'), QA('exceedPermissions'), QA('turnOffRisky'), QA('tricked')],
    },

    { kind: 'heading', level: 2, id: 'security-data', textKey: 'docs.faq.group.security.heading' },
    {
      kind: 'definitions',
      items: [
        QA('dataLive'),
        QA('otherCustomers'),
        QA('trainAi'),
        QA('staffSee'),
        QA('deleteData'),
      ],
    },

    { kind: 'heading', level: 2, id: 'the-widget', textKey: 'docs.faq.group.widget.heading' },
    {
      kind: 'definitions',
      items: [QA('breakSite'), QA('widgetRead'), QA('matchBrand')],
    },

    {
      kind: 'heading',
      level: 2,
      id: 'billing-languages',
      textKey: 'docs.faq.group.billing.heading',
    },
    {
      kind: 'definitions',
      items: [QA('cost'), QA('languages'), QA('mcpClients')],
    },

    {
      kind: 'callout',
      tone: 'tip',
      titleKey: 'docs.faq.more.title',
      textKey: 'docs.faq.more.body',
      links: {
        boundaries: {
          target: { docSlug: 'boundaries' },
          labelKey: 'docs.faq.more.link.boundaries',
        },
        security: {
          target: { docSlug: 'trust/security' },
          labelKey: 'docs.faq.more.link.security',
        },
      },
    },
  ],
};
